import {
    IonCard,
    IonItem,
    IonLabel,
    IonNote,
    IonSelect,
    IonSelectOption,
    IonText,
    IonToggle,
} from "@ionic/react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DobModal } from "../../../../modals/DobModal";
import { LeaderboardNameFormat } from "../../../../models/userProfile";
import { RootState } from "../../../../redux/store";
import { saveDob } from "../../../../services/account/dateOfBirthService";
import {
    getLeaderboardPreferences,
    meetsMinimumAge,
    nameFormatOptions,
    optInErrorKey,
    setLeaderboardPreferences,
} from "../../../../services/leaderboardOptInService";

/**
 * Venue leaderboard settings (SCORZA-739). **On by default** for adults — this card is the only
 * control, and the only way to leave.
 *
 * Deliberately not an `onPreferenceChange` row: this is enforced by the server, so it is written
 * to the profile via `setLeaderboardPreferences` and never to the device-local `ConfigPhone`
 * settings.
 *
 * The display name is a fixed set of *derived* options rather than a text field. Each option is
 * labelled with the name it would actually publish, so the choice is concrete, but the user never
 * supplies the string — the server derives it from their profile name and refuses a
 * client-supplied one, because free text on an unmoderated public board is an impersonation
 * vector.
 *
 * Under-18s cannot take part at all and cannot turn it on: the controls render disabled with an
 * explanation rather than being hidden, because a user who cannot find the setting reasonably
 * assumes they are on the board.
 */
export const LeaderboardsSettings: FC = () => {
    const { t } = useTranslation();

    const profile = useSelector(
        (state: RootState) => state.currentUser?.profile
    );
    const stored = getLeaderboardPreferences(profile);
    const options = nameFormatOptions(profile);

    const [participating, setParticipating] = useState(!stored.optOut);
    const [nameFormat, setNameFormat] = useState<LeaderboardNameFormat>(
        stored.nameFormat
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [askForDob, setAskForDob] = useState(false);

    // A missing dob is only "too young" once we have asked for it — until then it is a legacy
    // account we can still recover, so the modal offers that instead of the dead end.
    const ageBlocked = !!profile?.dob && !meetsMinimumAge(profile);

    // Keep in step with the profile once it lands from the server (or changes elsewhere).
    useEffect(() => {
        const current = getLeaderboardPreferences(profile);
        setParticipating(!current.optOut);
        setNameFormat(current.nameFormat);
    }, [profile?.leaderboards?.optOut, profile?.leaderboards?.nameFormat]);

    // `on` is passed explicitly rather than read from the closure so the dob-first flow can
    // hand in the profile it has just updated instead of the stale one from this render.
    const persist = async (
        change: { optOut?: boolean; nameFormat?: LeaderboardNameFormat },
        on = profile
    ) => {
        if (!on) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await setLeaderboardPreferences(on, change);
        } catch (err) {
            // The server fails closed (under 18, or no date of birth on the profile), so a
            // rejected change must revert the control rather than pretend it worked.
            const current = getLeaderboardPreferences(profile);
            setParticipating(!current.optOut);
            setNameFormat(current.nameFormat);
            setError(t(optInErrorKey(err)));
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = (checked: boolean) => {
        if (!profile || checked === participating) {
            return;
        }
        setParticipating(checked);

        // The age gate reads `dob`, and the server fails closed, so rejoining without one is a
        // guaranteed rejection. Ask first rather than round-trip to an error the user can do
        // nothing about from this screen. Leaving is never gated on anything.
        if (checked && !profile.dob) {
            setError(null);
            setAskForDob(true);
            return;
        }

        persist({ optOut: !checked });
    };

    const handleNameFormat = (format: LeaderboardNameFormat) => {
        if (!profile || format === nameFormat) {
            return;
        }
        setNameFormat(format);
        // Sent on its own: the server leaves participation as it was, so changing the name
        // cannot rejoin someone who has left.
        persist({ nameFormat: format });
    };

    /** Save the date of birth first, then retry the change it was blocking. */
    const handleDobEntered = async (date: string) => {
        setAskForDob(false);
        if (!profile || !date) {
            setParticipating(!getLeaderboardPreferences(profile).optOut);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            // Reuses the existing dob write path, which also regenerates server stats — dob
            // feeds heart-rate zones, so that is correct whenever it changes.
            await saveDob(date, profile);
        } catch (err) {
            setError(t("leaderboards.Settings.errorGeneric"));
            setParticipating(!getLeaderboardPreferences(profile).optOut);
            setSaving(false);
            return;
        }
        setSaving(false);
        // Send the dob along with the change. The server age-gates against the profile the
        // request will leave behind, so carrying it here means the gate cannot race the write
        // we just made, and the PUT can't blank the dob back out either.
        await persist({ optOut: false }, { ...profile, dob: date });
    };

    const controlsDisabled = saving || !profile || ageBlocked;

    return (
        <IonCard>
            <IonItem
                className="item-title-container rounded-corners-top"
                lines="full"
            >
                {t("leaderboards.Settings.title")}
            </IonItem>

            {ageBlocked && (
                <IonItem className="item-title" lines="full">
                    <IonText data-testid="leaderboards-age-blocked">
                        <p>{t("leaderboards.Settings.ageRestrictedNotice")}</p>
                    </IonText>
                </IonItem>
            )}

            <IonItem className="item-title" lines="full">
                <IonLabel>{t("leaderboards.Settings.optInLabel")}</IonLabel>
                <IonToggle
                    data-testid="leaderboards-opt-in-toggle"
                    checked={participating && !ageBlocked}
                    disabled={controlsDisabled}
                    onIonChange={(e) => handleToggle(e.detail.checked)}
                />
            </IonItem>

            <IonItem
                className="item-title"
                lines="full"
                data-testid="leaderboards-name-format-item"
            >
                <IonLabel>
                    {t("leaderboards.Settings.displayNameLabel")}
                </IonLabel>
                <IonSelect
                    className="select-text"
                    data-testid="leaderboards-name-format"
                    value={nameFormat}
                    // Nothing to apply it to while they are off the boards.
                    disabled={controlsDisabled || !participating}
                    interfaceOptions={{
                        header: t("leaderboards.Settings.displayNameLabel"),
                    }}
                    onIonChange={(e) => handleNameFormat(e.detail.value)}
                >
                    {options.map((option) => (
                        <IonSelectOption
                            key={option.format}
                            value={option.format}
                        >
                            {option.displayName ||
                                t(
                                    "leaderboards.Settings.nameFormatUnavailable"
                                )}
                        </IonSelectOption>
                    ))}
                </IonSelect>
            </IonItem>

            {error && (
                <IonItem className="item-title" lines="none">
                    <IonText color="danger" data-testid="leaderboards-error">
                        {error}
                    </IonText>
                </IonItem>
            )}

            <IonItem className="item-title rounded-corners-bottom" lines="none">
                <IonNote
                    className="ion-text-wrap"
                    data-testid="leaderboards-visibility-note"
                >
                    <p>{t("leaderboards.Settings.visibilityNote")}</p>
                </IonNote>
            </IonItem>

            <DobModal
                openDobModal={askForDob}
                onDismiss={() => {
                    setAskForDob(false);
                    setParticipating(
                        !getLeaderboardPreferences(profile).optOut
                    );
                }}
                dob={handleDobEntered}
                titleKey="leaderboards.Settings.dobPromptTitle"
                descriptionKeys={["leaderboards.Settings.dobPromptBody"]}
            />
        </IonCard>
    );
};
